import { BigNum, SpotMarketConfig } from '@velocity-exchange/sdk';
import { VaultDepositor } from '@velocity-exchange/vaults-sdk';
import { MarketId, UIMarket } from '@velocity-exchange/common';
import { useCallback, useMemo, useState } from 'react';
import { PeriodApy, PeriodApys } from 'src/@types/vaults';
import { BalanceDisplayType } from 'src/@types/types';
import { VAULT_NOTIONAL_GROWTH_STRATEGY_INFO_TEXT } from 'src/constants/vaults/misc';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { useAllVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import { getApyToUse, getAuthorityVaultBalance } from 'src/utils/vault/math';
import { cm, cj } from 'src/utils/classNames';
import {
	useDrawersStore,
	useDrawerToggler,
} from '../../../stores/useDrawersStore';
import { SortDirection } from '../../Icons/SortIcon';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import MarketIcon from '../../Utils/MarketIcon';
import VaultViewActions from '../VaultViewActions';
import { VaultInfoCell } from './VaultTableCells';
import {
	useVaultStatsWithDepositors,
	VaultStatsWithDepositor,
} from '../../../hooks/vaults/useVaultStatsWithDepositors';
import { VaultTableCurrentPositionCard } from '../VaultCard';

import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import { useVaultDepositorEarnings } from 'src/hooks/vaults/useVaultDepositorEarnings';
import { calcVaultDepositorNotionalPnl } from 'src/utils/vault/vaultDepositor';
import { useVaultDepositorHistories } from 'src/hooks/query/vaults/base/useVaultDepositorHistories';
import { VaultDepositorHistoriesLookup } from 'src/hooks/query/vaults/types';
import SortCaret from '../../Tables/SortCaret';
import InlineLoadingBar from '../../InlineLoadingBar/InlineLoadingBar';
import Divider from 'src/components/primitives/Divider/Divider';
import { getApyColumnHeader } from '../utils';
import { useRoiForVault } from '../hooks';
import { MySortableHeaderType } from '../type';
import Env from 'src/environmentVariables/EnvironmentVariables';

const FORCE_TEST_NO_ACTIVE_POSITIONS = false;
const FORCE_TEST_NO_CLOSED_POSITIONS = false;

type SortableHeader =
	| null
	| ({
			header: string;
	  } & (
			| {
					sortable: true;
					type: MySortableHeaderType;
			  }
			| {
					sortable: false;
			  }
	  ));

const getHeaders = (
	targetApyPeriod = '90D',
	isClosedPositions = false,
	isPortfolioPage = false
): SortableHeader[] => {
	if (isPortfolioPage) {
		return [
			{ header: 'Vault', sortable: false },
			{
				header: 'Your Balance',
				sortable: true,
				type: 'balance' as MySortableHeaderType,
			},
			{
				header: 'Your Earnings',
				sortable: true,
				type: 'earnings' as MySortableHeaderType,
			},
			{ header: 'Action', sortable: false },
		] as const;
	}

	return [
		{ header: 'Vault', sortable: false },
		...(isClosedPositions
			? [null, null]
			: [
					{
						header: getApyColumnHeader(targetApyPeriod),
						sortable: true,
						type: 'apy' as MySortableHeaderType,
					},
					{
						header: 'Your Balance',
						sortable: true,
						type: 'balance' as MySortableHeaderType,
					},
				]),
		...(isClosedPositions
			? [null]
			: [{ header: 'Your ROI', sortable: false as const }]),
		{
			header: 'Your Earnings',
			sortable: true,
			type: 'earnings' as MySortableHeaderType,
		},
		{ header: 'Action', sortable: false },
	] as const;
};

const getEarningsFromVaultStat = (
	vaultStatAndDepositor: VaultStatsWithDepositor,
	spotMarkets: SpotMarketConfig[],
	getOraclePriceForMarket: (
		marketId: MarketId | undefined
	) => BigNum | undefined,
	historiesLookup: VaultDepositorHistoriesLookup
) => {
	const vaultPubkey =
		vaultStatAndDepositor.vaultStat.uiVaultConfig.vaultPubkeyString;
	const oraclePrice = getOraclePriceForMarket(
		MarketId.createSpotMarket(
			vaultStatAndDepositor.vaultStat.uiVaultConfig.depositAsset
		)
	);

	const earnings = calcVaultDepositorNotionalPnl({
		vaultDepositor: vaultStatAndDepositor.depositor
			?.vaultDepositor as VaultDepositor,
		vault: vaultStatAndDepositor.vaultStat.vaultAccount,
		oraclePrice,
		vaultAssetPrecision:
			spotMarkets[vaultStatAndDepositor.vaultStat.uiVaultConfig.depositAsset]
				.precisionExp,
		vaultStats: vaultStatAndDepositor.vaultStat,
		vaultDepositorHistory: historiesLookup[vaultPubkey] ?? [],
	});

	return earnings;
};

const VaultRow = ({
	vaultStatAndDepositor,
	apyPeriod,
	isClosedPositions,
	isPortfolioPage,
}: {
	vaultStatAndDepositor: VaultStatsWithDepositor;
	apyPeriod: keyof PeriodApys;
	vaultDepositorAccounts: VaultDepositor[];
	isClosedPositions?: boolean;
	isPortfolioPage?: boolean;
}) => {
	const { apy, isUsingTemporaryApy, apyString } = getApyToUse(
		vaultStatAndDepositor.vaultStat.uiVaultConfig,
		vaultStatAndDepositor.vaultStat.apys[apyPeriod as keyof PeriodApys],
		vaultStatAndDepositor.vaultStat.numOfVaultSnapshots ?? 0
	);

	const authorityVaultBalance = getAuthorityVaultBalance(
		[vaultStatAndDepositor.depositor?.vaultDepositor as VaultDepositor],
		vaultStatAndDepositor.vaultStat,
		'base'
	);

	const getOraclePriceForMarket = useGetOraclePriceForMarket();

	const toggleVaultDetails = useDrawerToggler('vaultDetails');
	const set = useDrawersStore((state) => state.set);

	const handleViewVault = () => {
		set((store) => {
			store.vaultDetails.vault = vaultStatAndDepositor.vaultStat.uiVaultConfig;
		});
		toggleVaultDetails(true);
	};

	const roi = useRoiForVault(vaultStatAndDepositor);

	const isNotionalGrowthStrategy =
		vaultStatAndDepositor.vaultStat.uiVaultConfig.isNotionalGrowthStrategy;
	const { basePnl, notionalPnl } = useVaultDepositorEarnings(
		vaultStatAndDepositor.vaultStat.uiVaultConfig.vaultPubkeyString,
		vaultStatAndDepositor.depositor?.vaultDepositor as VaultDepositor,
		vaultStatAndDepositor.vaultStat.vaultAccount
	);
	const earnings = isNotionalGrowthStrategy ? notionalPnl : basePnl;

	const earningsMarketSymbol = vaultStatAndDepositor.vaultStat.uiVaultConfig
		.isNotionalGrowthStrategy
		? 'USDC'
		: UIMarket.createSpotMarket(
				vaultStatAndDepositor.vaultStat.uiVaultConfig.depositAsset
			).market.symbol;

	const authorityVaultBalanceNotional = authorityVaultBalance.mul(
		getOraclePriceForMarket(
			MarketId.createSpotMarket(
				vaultStatAndDepositor.vaultStat.uiVaultConfig.depositAsset
			)
		) as BigNum
	);

	const style = useMemo(() => {
		const numCols = isPortfolioPage ? 4 : 6;

		return { gridColumn: `span ${numCols} / span ${numCols}` };
	}, [isPortfolioPage]);

	return (
		<div
			className={cj(
				'grid grid-cols-subgrid',
				'px-md py-lg',
				'cursor-pointer bg-container-bg hover:bg-button-disabled'
			)}
			style={style}
			onClick={handleViewVault}
		>
			<VaultInfoCell
				uiVaultConfig={vaultStatAndDepositor.vaultStat.uiVaultConfig}
				numOfVaultSnapshots={
					vaultStatAndDepositor.vaultStat.numOfVaultSnapshots
				}
			/>

			{!isPortfolioPage &&
				(isClosedPositions ? (
					<div />
				) : (
					<div className="flex items-center typo-label-sm">
						<div
							className={cm(
								'flex items-center gap-1',
								isUsingTemporaryApy
									? 'text-purple-link'
									: apy >= 0
										? 'text-text-positive-green-button'
										: 'text-text-negative-red-button'
							)}
						>
							<span>{apyString}</span>
							{isUsingTemporaryApy && <span>*</span>}
						</div>
					</div>
				))}

			{isClosedPositions ? (
				<div />
			) : (
				<div className="flex items-center">
					{authorityVaultBalance.eqZero() ? (
						<span className="typo-body-xs text-text-disabled">-</span>
					) : (
						<div className="flex flex-col gap-xs">
							<div className="flex items-center gap-xs typo-label-sm">
								<MarketIcon
									marketSymbol={
										UIMarket.createSpotMarket(
											vaultStatAndDepositor.vaultStat.uiVaultConfig.depositAsset
										).market.symbol
									}
									className="h-4 w-4"
								/>
								<span
									className={cj(
										authorityVaultBalance.eqZero()
											? 'text-text-disabled'
											: 'text-text-default'
									)}
								>
									{earningsMarketSymbol === 'USDC'
										? authorityVaultBalance.toNum().toLocaleString(Env.locale, {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											})
										: authorityVaultBalance.toMillified(4)}
								</span>
							</div>

							<span className="typo-body-xs text-text-secondary">
								{authorityVaultBalanceNotional.toNotional()}
							</span>
						</div>
					)}
				</div>
			)}

			{!isPortfolioPage &&
				(isClosedPositions ? (
					<div />
				) : (
					<div className="flex items-center typo-label-sm">
						<span className="text-text-default">
							{roi ? <span>{roi.toFixed(2)}%</span> : <span>-</span>}
						</span>
					</div>
				))}

			<div className="flex items-center">
				<div className="flex flex-col gap-y-xs">
					<div className="flex items-center gap-xs typo-label-sm">
						<MarketIcon
							marketSymbol={earningsMarketSymbol}
							className="h-4 w-4"
						/>
						<span
							className={cj(
								earnings.eqZero() ? 'text-text-disabled' : 'text-text-default'
							)}
						>
							{earnings.toNum().toLocaleString(Env.locale, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</span>
						{vaultStatAndDepositor.vaultStat.uiVaultConfig
							.isNotionalGrowthStrategy && (
							<Tooltip content={VAULT_NOTIONAL_GROWTH_STRATEGY_INFO_TEXT} />
						)}
					</div>
					{!isNotionalGrowthStrategy && (
						<span className="typo-body-xs text-text-secondary">
							{basePnl
								.mul(
									getOraclePriceForMarket(
										MarketId.createSpotMarket(
											vaultStatAndDepositor.vaultStat.uiVaultConfig.depositAsset
										)
									)
								)
								.toNotional()}
						</span>
					)}
				</div>
			</div>

			<div className="flex items-center justify-end">
				<VaultViewActions
					uiVaultConfig={vaultStatAndDepositor.vaultStat.uiVaultConfig}
					condensed={true}
					type="manage"
					isClosedPosition={isClosedPositions}
				/>
			</div>
		</div>
	);
};

function sortVaults(
	a: VaultStatsWithDepositor,
	b: VaultStatsWithDepositor,
	sortDirection: SortDirection,
	sortType: MySortableHeaderType,
	targetApyPeriod: string,
	vaultDepositorAccounts: VaultDepositor[],
	getOraclePrice: (marketId: MarketId | undefined) => BigNum | undefined,
	spotMarkets: SpotMarketConfig[],
	historiesLookup: VaultDepositorHistoriesLookup
) {
	const apySort = () => {
		const { apy: vaultAApy } = getApyToUse(
			a.vaultStat.uiVaultConfig,
			a.vaultStat.apys[targetApyPeriod as keyof PeriodApys],
			a.vaultStat.numOfVaultSnapshots ?? 0
		);
		const { apy: vaultBApy } = getApyToUse(
			b.vaultStat.uiVaultConfig,
			b.vaultStat.apys[targetApyPeriod as keyof PeriodApys],
			b.vaultStat.numOfVaultSnapshots ?? 0
		);

		const res = vaultBApy - vaultAApy;
		return sortDirection === 'ascending' ? res * -1 : res; // default to descending
	};

	const balanceSort = () => {
		const authorityVaultBalanceA = getAuthorityVaultBalance(
			vaultDepositorAccounts,
			a.vaultStat,
			'quote',
			getOraclePrice
		);
		const authorityVaultBalanceB = getAuthorityVaultBalance(
			vaultDepositorAccounts,
			b.vaultStat,
			'quote',
			getOraclePrice
		).shiftTo(authorityVaultBalanceA.precision);

		const res = authorityVaultBalanceB.sub(authorityVaultBalanceA).toNum();
		return sortDirection === 'ascending' ? res * -1 : res; // default to descending
	};

	const earningsSort = () => {
		const earningsA = getEarningsFromVaultStat(
			a,
			spotMarkets,
			getOraclePrice,
			historiesLookup
		).toNum();
		const earningsB = getEarningsFromVaultStat(
			b,
			spotMarkets,
			getOraclePrice,
			historiesLookup
		).toNum();
		const res = earningsB - earningsA;
		return sortDirection === 'ascending' ? res * -1 : res; // default to descending
	};

	switch (sortType) {
		case 'apy':
			return apySort();
		case 'balance':
			return balanceSort();
		case 'earnings':
			return earningsSort();
	}
}

const NoActivePositionsSpacer = () => {
	return (
		<div className="flex items-center justify-center rounded-sm bg-container-bg p-8">
			<span className="typo-body-sm text-text-secondary">
				No active positions
			</span>
		</div>
	);
};

export const MyVaultPositionsContent = (props: {
	type: 'verified' | 'community' | 'my_positions';
	vaultDepositorAccounts: VaultDepositor[];
	targetApyPeriod: PeriodApy;
	targetVaultManager: string;
	targetDepositAsset: string;
	sortType: MySortableHeaderType;
	setSortType: (type: MySortableHeaderType) => void;
	noClosedPositions?: boolean;
	isPortfolioPage?: boolean;
}) => {
	const getOraclePrice = useGetOraclePriceForMarket();
	const spotMarkets = useSpotMarkets();

	const { isLoading } = useAllVaultStats();
	const areInitialVaultsStatsLoaded = !isLoading;
	const isMobile = useIsMobileScreenSize();

	const [balanceDisplayType, setBalanceDisplayType] =
		useState<BalanceDisplayType>('base');

	const [sortDirection, setSortDirection] =
		useState<SortDirection>('descending');
	const { targetVaultManager, targetDepositAsset, targetApyPeriod } = props;

	const toggleBalanceDisplayType = useCallback(() => {
		setBalanceDisplayType((prev) => (prev === 'base' ? 'notional' : 'base'));
	}, []);

	const setDefaultSort = () => {
		props.setSortType('apy');
		setSortDirection('descending');
	};

	const depositedOnly = props.type === 'my_positions';

	const ONLY_SHOW_BALANCES_GT_ZERO = false;

	const vaultStatsAndDepositRecords = useVaultStatsWithDepositors();
	const { data: historiesLookup } = useVaultDepositorHistories();

	const filteredVaultsStatsList = useMemo(() => {
		return vaultStatsAndDepositRecords
			.filter(({ depositor }) => {
				if (depositedOnly) {
					return (
						depositor &&
						(ONLY_SHOW_BALANCES_GT_ZERO
							? depositor.vaultDepositor.vaultShares.gtn(0)
							: true)
					);
				}
				return true;
			})
			.filter(({ vaultStat }) => {
				if (targetVaultManager === 'All') {
					return true;
				}

				return vaultStat.uiVaultConfig.vaultManager.name === targetVaultManager;
			})
			.filter(({ vaultStat }) => {
				if (targetDepositAsset === 'All') {
					return true;
				}

				return vaultStat.uiVaultConfig.depositAsset === +targetDepositAsset;
			});
	}, [
		vaultStatsAndDepositRecords,
		props.type,
		targetVaultManager,
		targetDepositAsset,
	]);

	const sortedVaultRecordsList = filteredVaultsStatsList.sort((a, b) =>
		sortVaults(
			a,
			b,
			sortDirection,
			props.sortType,
			targetApyPeriod,
			props.vaultDepositorAccounts,
			getOraclePrice,
			spotMarkets,
			historiesLookup ?? {}
		)
	);

	const activeVaultRecordsList = sortedVaultRecordsList.filter(
		({ depositor }) => depositor?.vaultDepositor.vaultShares.gtn(0)
	);

	const closedVaultRecordsList = sortedVaultRecordsList.filter(
		({ depositor }) => depositor?.vaultDepositor.vaultShares.eqn(0)
	);

	return (
		<div className="flex w-full flex-col gap-4">
			{isMobile ? (
				<div className="flex flex-col gap-4">
					{!FORCE_TEST_NO_ACTIVE_POSITIONS &&
					activeVaultRecordsList.length > 0 ? (
						activeVaultRecordsList.map((vault) => {
							return (
								<VaultTableCurrentPositionCard
									key={vault.vaultStat.uiVaultConfig.vaultPubkeyString}
									vaultStatAndDepositor={vault}
									uiVaultConfig={vault.vaultStat.uiVaultConfig}
									apyPeriod={targetApyPeriod}
									isClosedPosition={false}
									balanceDisplayType={balanceDisplayType}
									onToggleBalanceDisplayType={toggleBalanceDisplayType}
									isPortfolioPage={props.isPortfolioPage}
								/>
							);
						})
					) : (
						<NoActivePositionsSpacer />
					)}
					{!props.noClosedPositions &&
						!FORCE_TEST_NO_CLOSED_POSITIONS &&
						closedVaultRecordsList.length > 0 && (
							<>
								<Divider />

								<div className="rounded-sm bg-button-disabled p-md typo-label-sm text-text-default">
									Closed Positions
								</div>

								{closedVaultRecordsList.map((vault) => {
									return (
										<VaultTableCurrentPositionCard
											key={vault.vaultStat.uiVaultConfig.vaultPubkeyString}
											vaultStatAndDepositor={vault}
											uiVaultConfig={vault.vaultStat.uiVaultConfig}
											apyPeriod={targetApyPeriod}
											isClosedPosition={true}
											balanceDisplayType={balanceDisplayType}
											onToggleBalanceDisplayType={toggleBalanceDisplayType}
										/>
									);
								})}
							</>
						)}
				</div>
			) : (
				<div className="flex w-full flex-col gap-y-lg">
					{!FORCE_TEST_NO_ACTIVE_POSITIONS &&
					activeVaultRecordsList.length > 0 ? (
						<MyVaultPositionsTable
							sortedVaultRecordsList={activeVaultRecordsList}
							areInitialVaultsStatsLoaded={areInitialVaultsStatsLoaded}
							sortType={props.sortType}
							setSortType={props.setSortType}
							sortDirection={sortDirection}
							setSortDirection={setSortDirection}
							setDefaultSort={setDefaultSort}
							targetApyPeriod={targetApyPeriod}
							vaultDepositorAccounts={props.vaultDepositorAccounts}
							isClosedPositions={false}
							isPortfolioPage={props.isPortfolioPage}
						/>
					) : (
						<NoActivePositionsSpacer />
					)}
					{!props.noClosedPositions &&
						!FORCE_TEST_NO_CLOSED_POSITIONS &&
						closedVaultRecordsList.length > 0 && (
							<>
								<Divider />
								<MyVaultPositionsTable
									topContent={{
										label: 'Closed Positions',
									}}
									sortedVaultRecordsList={closedVaultRecordsList}
									areInitialVaultsStatsLoaded={areInitialVaultsStatsLoaded}
									sortType={props.sortType}
									setSortType={props.setSortType}
									sortDirection={sortDirection}
									setSortDirection={setSortDirection}
									setDefaultSort={setDefaultSort}
									targetApyPeriod={targetApyPeriod}
									vaultDepositorAccounts={props.vaultDepositorAccounts}
									isClosedPositions={true}
								/>
							</>
						)}
				</div>
			)}
		</div>
	);
};

const MyVaultPositionsTable = ({
	sortedVaultRecordsList,
	areInitialVaultsStatsLoaded,
	sortType,
	setSortType,
	sortDirection,
	setSortDirection,
	targetApyPeriod,
	vaultDepositorAccounts,
	isClosedPositions = false,
	topContent,
	isPortfolioPage,
}: {
	sortedVaultRecordsList: VaultStatsWithDepositor[];
	areInitialVaultsStatsLoaded: boolean;
	sortType: MySortableHeaderType;
	setSortType: (type: MySortableHeaderType) => void;
	sortDirection: SortDirection;
	setSortDirection: (direction: SortDirection) => void;
	setDefaultSort: () => void;
	targetApyPeriod: PeriodApy;
	vaultDepositorAccounts: VaultDepositor[];
	isClosedPositions?: boolean;
	topContent?: {
		label: string;
	};
	isPortfolioPage?: boolean;
}) => {
	const outerContainerStyle = useMemo(() => {
		return {
			gridTemplateColumns: !isPortfolioPage
				? '307fr 192fr 192fr 192fr 192fr 204fr'
				: '1fr 1fr 1fr 120px',
		};
	}, [isPortfolioPage]);

	const innerContainerStyle = useMemo(() => {
		const numCols = (() => {
			if (!areInitialVaultsStatsLoaded) {
				return 12;
			}

			return isPortfolioPage ? 4 : 6;
		})();

		return {
			gridColumn: `span ${numCols} / span ${numCols}`,
		};
	}, [isPortfolioPage, areInitialVaultsStatsLoaded]);

	return (
		<div
			className="col-span-12 grid overflow-hidden rounded-sm bg-container-bg"
			style={outerContainerStyle}
		>
			{topContent && (
				<div
					className={cj(
						'flex flex-col gap-y-xs bg-button-disabled px-md py-lg'
					)}
					style={innerContainerStyle}
				>
					<div className="typo-label-sm text-text-default">
						{topContent.label}
					</div>
				</div>
			)}
			{sortedVaultRecordsList.length > 0 && (
				<div
					className={cm(
						'hidden',
						'px-md py-sm',
						'typo-body-xs text-text-secondary',
						'sm:grid sm:grid-cols-subgrid',
						'border-b border-stroke-divider',
						'bg-container-bg'
					)}
					style={innerContainerStyle}
				>
					{getHeaders(targetApyPeriod, isClosedPositions, isPortfolioPage).map(
						(header, index) => {
							if (!header) {
								return <div key={index} />;
							}

							const isSortHeader = header.sortable;
							const isApy = header.header.includes('APY');

							const headComponent = () => {
								if (!isApy) {
									return header.header;
								}

								return (
									<Tooltip
										content={`Annualised yield based on the past ${targetApyPeriod.toUpperCase()}, after deducting vault fees.`}
									>
										{header.header}
									</Tooltip>
								);
							};

							return (
								<div
									key={`header_${header.header}`.replace(/ /g, '')}
									className={cm(
										header.header === 'Action' && 'text-right',
										isSortHeader && 'flex cursor-pointer items-center gap-1'
									)}
									onClick={
										isSortHeader
											? () => {
													if (header.type === sortType) {
														const nextDirection =
															sortDirection === 'neutral'
																? 'descending'
																: sortDirection === 'descending'
																	? 'ascending'
																	: sortDirection === 'ascending'
																		? 'neutral'
																		: 'neutral';
														setSortDirection(nextDirection);
													} else {
														setSortType(header.type);
														setSortDirection('descending');
													}
												}
											: undefined
									}
								>
									{headComponent()}
									{isSortHeader && (
										<SortCaret
											state={
												header.type === sortType ? sortDirection : 'neutral'
											}
										/>
									)}
								</div>
							);
						}
					)}
				</div>
			)}
			{!areInitialVaultsStatsLoaded ? (
				<div className="col-span-12 flex h-100 w-full items-center justify-center rounded-sm bg-container-bg">
					<InlineLoadingBar />
				</div>
			) : (
				<>
					{sortedVaultRecordsList.length > 0 ? (
						sortedVaultRecordsList.map((vaultStatAndDepositor) => (
							<VaultRow
								key={
									vaultStatAndDepositor.vaultStat.uiVaultConfig
										.vaultPubkeyString
								}
								vaultStatAndDepositor={vaultStatAndDepositor}
								apyPeriod={targetApyPeriod as keyof PeriodApys}
								vaultDepositorAccounts={vaultDepositorAccounts}
								isClosedPositions={isClosedPositions}
								isPortfolioPage={isPortfolioPage}
							/>
						))
					) : (
						<div className="col-span-12 flex h-[400px] w-full items-center justify-center rounded-sm bg-container-bg typo-body-sm text-text-disabled">
							No vaults found
						</div>
					)}
				</>
			)}
		</div>
	);
};
